import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatStatus } from '@/lib/utils';
import { Plus, Edit2, Trash2, Save, X, Users, MapPin, Building2, FileText, Briefcase, RotateCcw, Archive } from 'lucide-react';
import { formatINRFull } from '@/lib/utils';

export default function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('regions');
  const canManage = ['DIRECTOR', 'HR_ADMIN'].includes(user?.role);

  const tabs = [
    { id: 'regions', label: 'Regions', icon: MapPin },
    { id: 'clients', label: 'Clients', icon: Building2 },
    ...(canManage ? [{ id: 'users', label: 'Users', icon: Users }] : []),
    { id: 'templates', label: 'Quotation Templates', icon: FileText },
    ...(canManage ? [{ id: 'ta-policies', label: 'TA Policies', icon: Briefcase }] : []),
    ...(canManage ? [{ id: 'deleted-items', label: 'Deleted Items', icon: Archive }] : []),
  ];

  return (
    <div data-testid="settings-page" className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Settings</h1>

      <div className="flex border-b border-slate-200 gap-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap
              ${tab === t.id ? 'border-[#1B5486] text-[#1B5486]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <t.icon size={16} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'regions' && <RegionsTab isDirector={canManage} />}
      {tab === 'clients' && <ClientsTab canEdit={canManage || user?.role === 'SALES'} />}
      {tab === 'users' && canManage && <UsersTab />}
      {tab === 'templates' && <TemplatesTab isDirector={canManage} />}
      {tab === 'ta-policies' && <TAPoliciesTab />}
      {tab === 'deleted-items' && canManage && <DeletedItemsTab />}
    </div>
  );
}

function RegionsTab({ isDirector }) {
  const [regions, setRegions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', code: '' });

  const fetch = () => api.get('/regions').then(r => setRegions(r.data.regions || []));
  useEffect(() => { fetch(); }, []);

  const add = async (e) => {
    e.preventDefault();
    await api.post('/regions', form);
    setForm({ name: '', code: '' }); setShowAdd(false); fetch();
  };

  const del = async (id) => { if (window.confirm('Delete?')) { await api.delete(`/regions/${id}`); fetch(); } };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Regions ({regions.length})</h3>
        {isDirector && <button onClick={() => setShowAdd(!showAdd)} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Plus size={14} /> Add</button>}
      </div>
      {showAdd && (
        <form onSubmit={add} className="flex gap-2 mb-4" data-testid="add-region-form">
          <input placeholder="Region name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field flex-1" />
          <input placeholder="Code (2-4)" value={form.code} onChange={e => setForm({...form, code: e.target.value})} required className="input-field w-20" maxLength={4} />
          <button type="submit" className="bg-[#1B5486] text-white px-3 py-2 rounded-md text-sm">Add</button>
          <button type="button" onClick={() => setShowAdd(false)} className="border border-slate-200 px-3 py-2 rounded-md text-sm"><X size={14} /></button>
        </form>
      )}
      <div className="divide-y divide-slate-100">
        {regions.map(r => (
          <div key={r.id} className="flex items-center justify-between py-2.5">
            <div><span className="text-sm font-medium text-slate-900">{r.name}</span> <span className="text-xs text-slate-400 ml-2">{r.code}</span></div>
            {isDirector && <button onClick={() => del(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ClientsTab({ canEdit }) {
  const [clients, setClients] = useState([]);
  const [regions, setRegions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { name: '', short_name: '', division: '', region_id: '', contact_person: '', contact_phone: '', contact_email: '', address: '', gstin: '' };
  const [form, setForm] = useState(emptyForm);

  const fetch = () => api.get('/clients').then(r => setClients(r.data.clients || []));
  useEffect(() => { fetch(); api.get('/regions').then(r => setRegions(r.data.regions || [])); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/clients/${editId}`, form);
      } else {
        await api.post('/clients', form);
      }
      setForm(emptyForm); setShowAdd(false); setEditId(null); fetch();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (c) => {
    setForm({ name: c.name || '', short_name: c.short_name || '', division: c.division || '', region_id: c.region_id || '', contact_person: c.contact_person || '', contact_phone: c.contact_phone || '', contact_email: c.contact_email || '', address: c.address || '', gstin: c.gstin || '' });
    setEditId(c.id); setShowAdd(true);
  };

  const del = async (id) => { if (window.confirm('Delete this client?')) { await api.delete(`/clients/${id}`); fetch(); } };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Clients ({clients.length})</h3>
        {canEdit && <button onClick={() => { setForm(emptyForm); setEditId(null); setShowAdd(!showAdd); }} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Plus size={14} /> Add</button>}
      </div>
      {showAdd && (
        <form onSubmit={handleSubmit} className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4 p-3 bg-slate-50 rounded-lg" data-testid="add-client-form">
          <input placeholder="Company Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field" />
          <input placeholder="Short Name" value={form.short_name} onChange={e => setForm({...form, short_name: e.target.value})} className="input-field" />
          <input placeholder="Division" value={form.division} onChange={e => setForm({...form, division: e.target.value})} className="input-field" />
          <select value={form.region_id} onChange={e => setForm({...form, region_id: e.target.value})} className="input-field">
            <option value="">Region</option>{regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
          <input placeholder="Contact Person" value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} className="input-field" />
          <input placeholder="Phone" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className="input-field" />
          <input placeholder="Email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} className="input-field" />
          <input placeholder="GSTIN" value={form.gstin} onChange={e => setForm({...form, gstin: e.target.value})} className="input-field" />
          <input placeholder="Address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="input-field" />
          <div className="flex gap-2">
            <button type="submit" data-testid="submit-client-btn" className="bg-[#1B5486] text-white px-3 py-2 rounded-md text-sm">{editId ? 'Update' : 'Add'} Client</button>
            <button type="button" onClick={() => { setShowAdd(false); setEditId(null); }} className="border border-slate-200 px-3 py-2 rounded-md text-sm">Cancel</button>
          </div>
        </form>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Name</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Short</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Division</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Region</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Contact</th>
            {canEdit && <th className="w-20"></th>}
          </tr></thead>
          <tbody>
            {clients.map(c => (
              <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
                <td className="py-2 px-3 font-medium text-slate-900">{c.name}</td>
                <td className="py-2 px-3 text-slate-500">{c.short_name || '-'}</td>
                <td className="py-2 px-3 text-slate-500">{c.division || '-'}</td>
                <td className="py-2 px-3 text-slate-500">{c.region_name || '-'}</td>
                <td className="py-2 px-3 text-slate-500">{c.contact_person || '-'}</td>
                {canEdit && (
                  <td className="py-2 px-3">
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(c)} data-testid={`edit-client-${c.id}`} className="text-slate-400 hover:text-[#1B5486]"><Edit2 size={14} /></button>
                      <button onClick={() => del(c.id)} data-testid={`delete-client-${c.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { name: '', email: '', password: '', role: 'VIEWER', phone: '', assigned_project_ids: [] };
  const [form, setForm] = useState(emptyForm);

  const fetchUsers = () => api.get('/users').then(r => setUsers(r.data.users || []));
  const fetchProjects = () => api.get('/projects').then(r => setProjects(r.data.projects || []));
  useEffect(() => { fetchUsers(); fetchProjects(); }, []);

  const toggleProject = (pid) => {
    setForm(prev => {
      const ids = prev.assigned_project_ids || [];
      return { ...prev, assigned_project_ids: ids.includes(pid) ? ids.filter(id => id !== pid) : [...ids, pid] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const payload = { name: form.name, role: form.role, phone: form.phone, assigned_project_ids: form.role === 'SUPERVISOR' ? form.assigned_project_ids : [] };
        if (form.password && form.password !== '********') payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
      } else {
        const payload = { ...form };
        if (form.role !== 'SUPERVISOR') payload.assigned_project_ids = [];
        await api.post('/users', payload);
      }
      setForm(emptyForm); setShowAdd(false); setEditId(null); fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: '********', role: u.role, phone: u.phone || '', assigned_project_ids: u.assigned_project_ids || [] });
    setEditId(u.id); setShowAdd(true);
  };

  const del = async (u) => {
    if (window.confirm(`Delete user ${u.name}? This cannot be undone.`)) {
      try { await api.delete(`/users/${u.id}`); fetchUsers(); }
      catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    }
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { is_active: !u.is_active });
    fetchUsers();
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Users ({users.length})</h3>
        <button onClick={() => { setForm(emptyForm); setEditId(null); setShowAdd(!showAdd); }} className="text-sm text-[#1B5486] hover:underline flex items-center gap-1"><Plus size={14} /> Add User</button>
      </div>
      {showAdd && (
        <form onSubmit={handleSubmit} className="mb-4 p-4 bg-slate-50 rounded-lg space-y-3" data-testid="add-user-form">
          <h3 className="text-sm font-semibold text-slate-800">{editId ? 'Edit User' : 'Add User'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <input placeholder="Full Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field" data-testid="user-name-input" />
            <input type="email" placeholder="Email *" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required disabled={!!editId} className={`input-field ${editId ? 'bg-slate-100 text-slate-500' : ''}`} data-testid="user-email-input" />
            <input type="password" placeholder={editId ? 'New password (leave to keep)' : 'Password'} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="input-field" />
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value, assigned_project_ids: []})} className="input-field" data-testid="user-role-select">
              <option value="DIRECTOR">Director</option>
              <option value="SALES">Sales</option>
              <option value="ACCOUNTANT">Accountant</option>
              <option value="SUPERVISOR">Supervisor</option>
              <option value="HR_ADMIN">HR Admin</option>
              <option value="VIEWER">Viewer</option>
            </select>
            <input type="tel" inputMode="numeric" pattern="[0-9]*" placeholder="Phone" value={form.phone} onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); setForm({...form, phone: v}); }} className="input-field" />
          </div>
          {form.role === 'SUPERVISOR' && (
            <div data-testid="assign-projects-section">
              <label className="block text-xs font-semibold text-slate-600 mb-2">Assign Projects</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-white">
                {projects.length === 0 && <p className="text-xs text-slate-400 col-span-3 text-center py-2">No projects found</p>}
                {projects.map(p => (
                  <label key={p.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs hover:bg-blue-50 transition-colors ${(form.assigned_project_ids || []).includes(p.id) ? 'bg-blue-50' : ''}`}>
                    <input type="checkbox" checked={(form.assigned_project_ids || []).includes(p.id)} onChange={() => toggleProject(p.id)} className="rounded border-slate-300 text-[#1B5486] focus:ring-[#1B5486]" />
                    <span className="font-medium text-slate-700">{p.project_code}</span>
                    <span className="text-slate-400 truncate">{p.name}</span>
                  </label>
                ))}
              </div>
              {(form.assigned_project_ids || []).length > 0 && (
                <p className="text-xs text-[#1B5486] mt-1">{form.assigned_project_ids.length} project(s) selected</p>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button type="submit" data-testid="submit-user-btn" className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm">{editId ? 'Update' : 'Add'} User</button>
            <button type="button" onClick={() => { setShowAdd(false); setEditId(null); }} className="border border-slate-200 px-4 py-2 rounded-md text-sm">Cancel</button>
          </div>
        </form>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-200">
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Name</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Email</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Role</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Phone</th>
            <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Status</th>
            <th className="w-28"></th>
          </tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="py-2 px-3 font-medium text-slate-900">{u.name}</td>
                <td className="py-2 px-3 text-slate-500">{u.email}</td>
                <td className="py-2 px-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[#1B5486]/10 text-[#1B5486]">{formatStatus(u.role)}</span></td>
                <td className="py-2 px-3 text-slate-500">{u.phone || '-'}</td>
                <td className="py-2 px-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {u.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="py-2 px-3">
                  <div className="flex gap-2 items-center">
                    <button onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`} className="text-slate-400 hover:text-[#1B5486]"><Edit2 size={14} /></button>
                    <button onClick={() => del(u)} data-testid={`delete-user-${u.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                    <button onClick={() => toggleActive(u)} className="text-xs text-slate-500 hover:text-[#1B5486] ml-1">
                      {u.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TemplatesTab({ isDirector }) {
  const [templates, setTemplates] = useState([]);
  const fetch = () => api.get('/quotation-templates').then(r => setTemplates(r.data.templates || []));
  useEffect(() => { fetch(); }, []);

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Quotation Templates ({templates.length})</h3>
      <div className="space-y-3">
        {templates.map(t => (
          <div key={t.id} className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-slate-900">{t.name}</h4>
              {t.is_default && <span className="text-xs px-2 py-0.5 rounded-full bg-[#1B5486]/10 text-[#1B5486]">Default</span>}
            </div>
            <p className="text-xs text-slate-500 mb-2">GST Note: {t.gst_note}</p>
            <div className="text-xs text-slate-500">
              <span className="font-medium">Terms & Conditions:</span>
              <ol className="list-decimal list-inside mt-1 space-y-0.5">
                {(t.terms_and_conditions || []).map((tc, i) => <li key={i}>{tc}</li>)}
              </ol>
            </div>
          </div>
        ))}
        {templates.length === 0 && <div className="text-center py-8 text-sm text-slate-400">No templates found</div>}
      </div>
    </div>
  );
}


const EMP_TYPES = ['TECHNICIAN', 'ENGINEER', 'SUPERVISOR', 'HELPER', 'DRIVER', 'OFFICE_STAFF'];

function TAPoliciesTab() {
  const [policies, setPolicies] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', employee_type: 'SUPERVISOR', daily_food: 0, daily_accommodation: 0, daily_local_transport: 0, effective_from: '', effective_to: '', is_active: true });

  const fetchPolicies = () => {
    api.get('/employees/ta-policies').then(r => setPolicies(r.data.policies || []));
  };
  useEffect(() => { fetchPolicies(); }, []);

  const resetForm = () => {
    setForm({ name: '', employee_type: 'SUPERVISOR', daily_food: 0, daily_accommodation: 0, daily_local_transport: 0, effective_from: '', effective_to: '', is_active: true });
    setShowCreate(false);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.patch(`/employees/ta-policies/${editId}`, form);
      } else {
        await api.post('/employees/ta-policies', form);
      }
      resetForm();
      fetchPolicies();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (p) => {
    setForm({ name: p.name, employee_type: p.employee_type, daily_food: p.daily_food, daily_accommodation: p.daily_accommodation, daily_local_transport: p.daily_local_transport, effective_from: p.effective_from || '', effective_to: p.effective_to || '', is_active: p.is_active });
    setEditId(p.id);
    setShowCreate(true);
  };

  const dailyTotal = (parseFloat(form.daily_food) || 0) + (parseFloat(form.daily_accommodation) || 0) + (parseFloat(form.daily_local_transport) || 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-800">Travel Allowance Policies</h2>
        <button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="add-ta-policy-btn"
          className="bg-[#1B5486] text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-1">
          <Plus size={14} /> Add Policy
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="ta-policy-form">
          <h3 className="text-sm font-semibold text-slate-900">{editId ? 'Edit' : 'Create'} TA Policy</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Policy Name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field w-full" data-testid="ta-policy-name" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Employee Type *</label>
              <select value={form.employee_type} onChange={e => setForm({...form, employee_type: e.target.value})} className="input-field w-full" data-testid="ta-policy-type">
                {EMP_TYPES.map(t => <option key={t} value={t}>{formatStatus(t)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Active</label>
              <select value={form.is_active ? 'true' : 'false'} onChange={e => setForm({...form, is_active: e.target.value === 'true'})} className="input-field w-full">
                <option value="true">Active</option><option value="false">Inactive</option></select></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Daily Food</label>
              <input type="number" value={form.daily_food} onChange={e => setForm({...form, daily_food: parseFloat(e.target.value) || 0})} className="input-field w-full" data-testid="ta-daily-food" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Daily Accommodation</label>
              <input type="number" value={form.daily_accommodation} onChange={e => setForm({...form, daily_accommodation: parseFloat(e.target.value) || 0})} className="input-field w-full" data-testid="ta-daily-accom" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Daily Local Transport</label>
              <input type="number" value={form.daily_local_transport} onChange={e => setForm({...form, daily_local_transport: parseFloat(e.target.value) || 0})} className="input-field w-full" data-testid="ta-daily-transport" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Daily Total</label>
              <div className="input-field w-full bg-blue-50 text-blue-700 font-semibold" data-testid="ta-daily-total">{formatINRFull(dailyTotal)}</div></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Effective From</label>
              <input type="date" value={form.effective_from} onChange={e => setForm({...form, effective_from: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Effective To</label>
              <input type="date" value={form.effective_to} onChange={e => setForm({...form, effective_to: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="save-ta-policy-btn" className="bg-[#1B5486] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1"><Save size={14} /> {editId ? 'Update' : 'Save'}</button>
            <button type="button" onClick={resetForm} className="border px-4 py-2 rounded-lg text-sm flex items-center gap-1"><X size={14} /> Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {policies.map(p => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between" data-testid={`ta-policy-${p.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-slate-900">{p.name}</h4>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {p.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{formatStatus(p.employee_type)}</span>
              </div>
              <div className="flex gap-3 mt-1 text-xs text-slate-500">
                <span>Food: {formatINRFull(p.daily_food)}</span>
                <span>Accom: {formatINRFull(p.daily_accommodation)}</span>
                <span>Transport: {formatINRFull(p.daily_local_transport)}</span>
                <span className="font-semibold text-slate-700">Total: {formatINRFull(p.daily_total)}/day</span>
              </div>
              {p.effective_from && <p className="text-[10px] text-slate-400 mt-0.5">Effective: {p.effective_from} {p.effective_to ? `to ${p.effective_to}` : 'onwards'}</p>}
            </div>
            <button onClick={() => startEdit(p)} className="text-slate-400 hover:text-[#1B5486]" data-testid={`edit-ta-policy-${p.id}`}><Edit2 size={14} /></button>
          </div>
        ))}
        {policies.length === 0 && <div className="text-center py-8 text-sm text-slate-400">No TA policies defined yet</div>}
      </div>
    </div>
  );
}


function DeletedItemsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = () => {
    setLoading(true);
    api.get('/deleted-items').then(r => setItems(r.data.items || [])).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchItems(); }, []);

  const restoreItem = async (entityType, itemId) => {
    if (!window.confirm('Restore this item?')) return;
    try {
      await api.post(`/restore/${entityType}/${itemId}`);
      fetchItems();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to restore'); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="deleted-items-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Deleted Items ({items.length})</h3>
        <button onClick={fetchItems} className="text-xs text-[#1B5486] hover:underline flex items-center gap-1"><RotateCcw size={12} /> Refresh</button>
      </div>
      {loading ? <div className="text-center py-8 text-sm text-slate-400">Loading...</div> :
        items.length === 0 ? <div className="text-center py-8 text-sm text-slate-400">No deleted items found</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Type</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Name / Code</th>
                <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Deleted At</th>
                <th className="w-20"></th>
              </tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={`${item.entity_type}-${item.id}`} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`deleted-item-${item.id}`}>
                    <td className="py-2 px-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(item.entity_type)}</span></td>
                    <td className="py-2 px-3 font-medium text-slate-900">{item.name || item.project_code || item.lead_code || item.id}</td>
                    <td className="py-2 px-3 text-slate-400 text-xs">{item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : '-'}</td>
                    <td className="py-2 px-3">
                      <button onClick={() => restoreItem(item.entity_type, item.id)} data-testid={`restore-${item.id}`}
                        className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 flex items-center gap-0.5">
                        <RotateCcw size={12} /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
